import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { BdAccountScopedPermissionCache } from '../../cache/account-scoped-permission-cache';
import { BdAdminPermissionsStore } from '../store/admin-permissions.store';
import {
  BD_ADMIN_PERMISSION_STORAGE_BUCKET,
  BdAdminPermission,
} from './admin-permission.const';

@Injectable({
  providedIn: 'root',
})
export class BdAdminPermissionService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentService = inject(BdAppEnvironmentService);
  private readonly _authService = inject(BdAuthenticationService);
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _store = new BdAdminPermissionsStore(
    this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
  );

  private readonly _cache = new BdAccountScopedPermissionCache<
    BdAdminPermission[]
  >(BD_ADMIN_PERMISSION_STORAGE_BUCKET, this._isBrowser);

  /**
   * Waits for the signed-in account before touching the cache: the account is
   * what makes a cached entry valid, and it is reported one synchronous step
   * after consumers start asking (see `BdAccountScopedPermissionCache`).
   */
  public getPermissions(force = false): Observable<BdAdminPermission[]> {
    return this._authService.accountInfo$.pipe(
      take(1),
      switchMap((account) =>
        this._cache.resolve(account?.homeAccountId ?? null, force, () =>
          this._fetchAllPermissions(),
        ),
      ),
    );
  }

  public containsPermission(
    permissionsToCheck: BdAdminPermission[],
  ): Observable<boolean> {
    return this.getPermissions().pipe(
      map((allPermissions) =>
        permissionsToCheck.every((singlePermission) =>
          allPermissions.includes(singlePermission),
        ),
      ),
    );
  }

  private _fetchAllPermissions(): Observable<BdAdminPermission[]> {
    return this._httpClient
      .get<BdAdminPermission[]>(this._store.findAllPermissions())
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Endpoint returns 404 when the user has no admin permissions.
          if (error.status === 404) {
            return of<BdAdminPermission[]>([]);
          }
          return throwError(() => error);
        }),
      );
  }
}
